import { Component, Input, computed, inject, signal } from '@angular/core';
import { ApexChart, ApexLegend, ApexResponsive, NgApexchartsModule } from 'ng-apexcharts';

import { LocaleService } from '../../../services/locale.service';
import { BreakdownItem } from '../../../types/analytics.types';

/**
 * Donut d'une ventilation analytics (`BreakdownItem[]`) : paiements par méthode
 * / par type, règlements par mode. La **valeur** de chaque part est le montant
 * (`value`), pas l'effectif.
 *
 * Les `key` back (nom de méthode, `metadataType`, mode de paiement) sont
 * résolues en libellé via `keyPrefix` : `keyPrefix + '.' + key` est passé au
 * dictionnaire, avec **repli sur la clé brute** (le nom de méthode « Wave »
 * n'a pas besoin de traduction). Passer `keyPrefix=''` pour afficher la clé
 * telle quelle.
 */
@Component({
  selector: 'app-analytics-donut',
  standalone: true,
  imports: [NgApexchartsModule],
  template: `
    @if (items.length === 0) {
      <div class="flex h-[220px] items-center justify-center text-sm text-gray-400 dark:text-gray-500">
        {{ emptyLabel }}
      </div>
    } @else {
      <apx-chart
        [series]="series()"
        [labels]="labels()"
        [chart]="chart"
        [colors]="colors"
        [legend]="legend"
        [responsive]="responsive"
        [dataLabels]="{ enabled: false }"
      />
    }
  `,
})
export class AnalyticsDonutComponent {
  private readonly locale = inject(LocaleService);
  private readonly _items = signal<BreakdownItem[]>([]);
  private readonly _metric = signal<'value' | 'count'>('value');

  @Input({ required: true }) set items(v: BreakdownItem[]) {
    this._items.set(v ?? []);
  }
  get items(): BreakdownItem[] {
    return this._items();
  }

  /**
   * Métrique des parts : `value` (montant, défaut — ventilations d'argent) ou
   * `count` (effectif — ex. répartition par transition de cycle de vie).
   */
  @Input() set metric(v: 'value' | 'count') {
    this._metric.set(v ?? 'value');
  }
  get metric(): 'value' | 'count' {
    return this._metric();
  }

  /** Préfixe i18n des `key` (ex. `metadataType`, `label.paymentMode`). */
  @Input() keyPrefix = '';
  @Input() emptyLabel = '';

  readonly series = computed<number[]>(() => {
    const metric = this._metric();
    return this._items().map((i) => (metric === 'count' ? i.count : i.value));
  });

  readonly labels = computed<string[]>(() =>
    this._items().map((i) =>
      this.keyPrefix ? this.locale.t(`${this.keyPrefix}.${i.key}`) : i.key,
    ),
  );

  readonly chart: ApexChart = {
    fontFamily: 'Outfit, sans-serif',
    type: 'donut',
    height: 220,
  };

  // Vert marque décliné + accents neutres, lisibles en clair comme en sombre.
  readonly colors = ['#0B8C00', '#7AD968', '#F59E0B', '#3B82F6', '#94A3B8'];

  readonly legend: ApexLegend = {
    position: 'bottom',
    fontFamily: 'Outfit, sans-serif',
    labels: { colors: '#9CA3AF' },
  };

  readonly responsive: ApexResponsive[] = [
    { breakpoint: 480, options: { chart: { height: 200 }, legend: { position: 'bottom' } } },
  ];
}
